"use client";

import { z } from "zod";
import {
  currency,
  Industries,
  industryOptions,
  loanTypes,
  loanUseOptions,
} from "@cxm/lib";
import { postcodeValidator } from "postcode-validator";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { DefaultValues } from "../utils/form-helpers";
import { Button } from "../components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/form";
import { Select } from "../components/select";
import { useConfirmDialogContext } from "../components/confirmdialog";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getApiBorrowersGetGlossaryOptions,
  postApiBorrowersEvaluateFundingOptionsMutation,
  postApiBorrowersOnboardAdditionalBorrowerMutation,
} from "@cxm/client/queries";
import {
  CompanyStage,
  FundingOption,
  FundingTimeline,
  GetApiBorrowersGetGlossaryResponse,
  LandingEvaluationResponse,
  LoanType,
  LoanUse,
} from "@cxm/client";
import {
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Slider } from "../components/slider";
import { Input } from "../components/input";
import { MoneyField, MoneyInput, SelectField } from "./form-inputs";
import { zodResolver } from "@hookform/resolvers/zod";
import Wheel from "./wheel";
import {
  FundabilityScoreCard,
  FundingOptionBreakdown,
  FundingOptionsTable,
  getFundabilityScoreStyles,
} from "./evaluation-results";
import debounce from "debounce";
import { EvaluationIntroBanner, IndicativeLoanOptionsCard } from "./evaluation-chrome";
import { defaultEvaluationIndustry } from "./evaluation-form-schemas";
import { getOnboardWidgetCta } from "./onboard-widget-cta";
const fundingTimelineOptions = [
  {
    label: "As soon as possible",
    value: FundingTimeline.AS_SOON_AS_POSSIBLE,
  },
  { label: "Within 1 month", value: FundingTimeline.WITHIN_ONE_MONTH },
  { label: "Within 3 months", value: FundingTimeline.WITHIN_THREE_MONTHS },
  { label: "Within 6 months", value: FundingTimeline.WITHIN_SIX_MONTHS },
];

const screenOneMetrics = [
  {
    Key: "TTMRevenue",
    Name: "Previous 12 Months Revenue",
    Min: 0,
    Max: 50000000,
    Description: "Total revenue for the past 12 months",
  },
  {
    Key: "TTMEbitda",
    Name: "Previous 12 Months Operating Profit",
    Min: -5000000,
    Max: 50000000,
    Description: "Profits before financing and accounting costs (EBITDA)",
  },
  {
    Key: "TTMNetIncome",
    Name: "Previous 12 Months Net Income",
    Min: -5000000,
    Max: 50000000,
    Description: "Total profit after all expenses and taxes for the past year",
  },
  {
    Key: "TotalInventory",
    Name: "Inventory Value",
    Min: 0,
    Max: 20000000,
    Description:
      "Estimated value of inventory. Use the second slider to set the value already pledged as collateral.",
    HasSecondKey: true,
    SecondKey: "EncumberedInventory",
    SecondName: "Value of inventory already pledged as collateral",
  },
  {
    Key: "TotalRealEstate",
    Name: "Real Estate Value",
    Min: 0,
    Max: 20000000,
    Description:
      "Estimated value of real estate. Use the second slider to set the value already pledged as collateral.",
    HasSecondKey: true,
    SecondKey: "EncumberedRealEstate",
    SecondName: "Value of real estate already pledged as collateral",
  },
  {
    Key: "TotalEquipment",
    Name: "Equipment Value",
    Min: 0,
    Max: 20000000,
    Description:
      "Estimated value of equipment. Use the second slider to set the value already pledged as collateral.",
    HasSecondKey: true,
    SecondKey: "EncumberedEquipment",
    SecondName: "Value of equipment already pledged as collateral",
  },
  {
    Key: "TotalDebt",
    Name: "Total Debt",
    Min: 0,
    Max: 10000000,
    Step: 50000,
    Description: "Total amount of all business debt",
  },
  {
    Key: "MonthlyDebtService",
    Name: "Monthly Debt Service",
    Min: 0,
    Step: 50000,
    Max: 2000000,
    Description: "Total monthly payments on all debt obligations",
  },
  {
    Key: "MonthlyAr",
    Name: "Average Monthly Accounts Receivable",
    Min: 0,
    Max: 1000000,
    Description:
      "Money owed to your business by customers, averaged over the past 12 months",
  },
  {
    Key: "PO",
    Name: "Average Monthly Purchase Orders",
    Min: 0,
    Max: 10000000,
    Description:
      "Average monthly purchase orders, averaged over the past 12 months",
  },
];

const industryNames = Object.values(Industries).map((
  industry,
) => (industry.displayName));

// Re-exported from the shared evaluation-results module so existing
// importers (triage-table, specialist-dashboard) keep resolving against
// this path. The implementation now lives in ./evaluation-results.
export { getFundabilityScoreStyles };

const industryEnum = z.enum(industryNames as [string, ...string[]]);

// Custom validation function for US zip codes
const validateUSZipCode = (zipCode: string) => {
  // Remove any whitespace
  const cleanZipCode = zipCode.trim();

  // Check if empty after trimming
  if (!cleanZipCode) {
    return false;
  }

  // Check if it's a valid US zip code (5 digits or 5+4 format)
  return postcodeValidator(cleanZipCode, "US");
};

const formOneModel = z.object({
  Industry: industryEnum,
  LoanAmount: z.number(),
  ZipCode: z.string()
    .min(1, "Zip code is required")
    .refine(validateUSZipCode, {
      message: "Please enter a valid US zip code (e.g., 12345 or 12345-6789)",
    }),
  LoanUse: z.string(),
  FundingTimeline: z.string(),
  CompanyStage: z.string(),
  ...Object.fromEntries(
    screenOneMetrics.flatMap((metric) => [
      [metric.Key, metric.Min == 0 ? z.number().min(0) : z.number()],
      ...(metric.HasSecondKey ? [[metric.SecondKey, z.number().min(0)]] : []),
    ]),
  ),
});

const formTwoModel = z.object({
  LoanUse: z.string(),
  LoanAmount: z.number(),
  FundingTimeline: z.string(),
});

// `formTwo` is wrapped around the wheel/results step and does NOT
// render a Funding Timeline Select — these defaults are effectively
// dead state. Keep them aligned with the referral wizard's
// "no pre-filled timeline" rule so a future revival of formTwo (or any
// code that reads `formTwoDefaults.FundingTimeline`) doesn't quietly
// reinherit the old "Within 6 months" default the stakeholder wanted
// removed. The visible formOne Select already defaults to blank because
// its `defaults` object intentionally omits `FundingTimeline`.
const formTwoDefaults: DefaultValues<FormTwoType> = {
  LoanUse: LoanUse.GROWTH_CAPITAL,
  LoanAmount: 1000000,
  FundingTimeline: "" as unknown as FundingTimeline,
};

type FormTwoType = z.infer<typeof formTwoModel>;
type FormOneType = z.infer<typeof formOneModel>;

const defaults: DefaultValues<FormOneType> = {
  Industry: defaultEvaluationIndustry,
  LoanAmount: 1000000,
  CompanyStage: CompanyStage.GROWTH,
  ...Object.fromEntries(
    screenOneMetrics.flatMap((metric) => [
      [metric.Key, 0],
      ...(metric.HasSecondKey ? [[metric.SecondKey, 0]] : []),
    ]),
  ),
};

type OnboardState = "start" | "calculated" | "done";
type OnboardStep = 1 | 2 | 3 | 4;

function OnboardWidget(
  { isLoggedIn, baseUrl, nonWidget, referralTag }: {
    initialGlossary?: GetApiBorrowersGetGlossaryResponse;
    baseUrl?: string;
    isLoggedIn?: boolean;
    nonWidget?: boolean;
    referralTag?: string;
  },
) {
  const applicationCta = getOnboardWidgetCta(referralTag);
  const [loading, setLoading] = useState<boolean>(false);
  const [hoveredType, setHoveredType] = useState<string>();
  const [onboardState, setOnboardState] = useState<OnboardState>("start");
  const [currentStep, setCurrentStep] = useState<OnboardStep>(1);
  const [lastEvalId, setLastEvalId] = useState<string>();
  const [selectedLoanType, setSelectedLoanType] = useState<LoanType>();
  const [drawBar, setDrawBar] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [resultCalculation, setResultCalculation] = useState<
    LandingEvaluationResponse
  >();
  const [recommendedLoanType, setRecommendedLoanType] = useState<
    LoanType
  >();
  const [topFundingOption, setTopFundingOption] = useState<FundingOption>();
  const [biggestFundingOptions, setBiggestFundingOption] = useState<
    FundingOption
  >();
  const [formOneLastData, setFormOneLastData] = useState<
    Record<string, any>
  >();
  const [isLocating, setIsLocating] = useState(false);
  const [netIncomeManuallyAdjusted, setNetIncomeManuallyAdjusted] = useState(
    false,
  );
  const lastSyncedEbitda = useRef<number>(0);
  const evaluationGeneration = useRef(0);

  const form = useForm<FormOneType>({
    resolver: zodResolver(formOneModel),
    defaultValues: defaults,
  });
  const formTwo = useForm<FormTwoType>({
    defaultValues: formTwoDefaults,
  });

  const fundingOptionsQuery = useMutation(
    postApiBorrowersEvaluateFundingOptionsMutation(),
  );

  const handleResize = () => {
    setIsMobile(window.innerWidth < 768); // Adjust the breakpoint as needed
  };

  const onboardBorrowerMutation = useMutation(
    postApiBorrowersOnboardAdditionalBorrowerMutation(),
  );

  const confirmDialog = useConfirmDialogContext();

  useEffect(() => {
    if (loading) {
      setTimeout(() => {
        setDrawBar(true);
      }, 10);
    } else {
      setDrawBar(false);
    }
  }, [loading]);

  useEffect(() => {
    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const secondForm = useForm({
    defaultValues: {
      loanType: recommendedLoanType,
      dummyField: "dummy",
    },
  });

  // Debounced calculate score function
  const debouncedCalculateScore = useCallback(
    debounce(
      (
        values: Record<string, any>,
        shouldFindBestLoanType = true,
        requestGeneration: number,
      ) => {
        calculateScore(values, shouldFindBestLoanType, requestGeneration);
      },
      1000,
    ),
    [],
  );

  const scheduleRecalculation = useCallback(
    (values: Record<string, any>, shouldFindBestLoanType = true) => {
      const requestGeneration = ++evaluationGeneration.current;
      setLastEvalId(undefined);
      debouncedCalculateScore(
        values,
        shouldFindBestLoanType,
        requestGeneration,
      );
    },
    [debouncedCalculateScore],
  );

  // Watch form changes and trigger debounced calculation if we've already calculated once
  useEffect(() => {
    if (resultCalculation) {
      const subscription = form.watch((values) => {
        if (values) {
          scheduleRecalculation(values as Record<string, any>, false);
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [resultCalculation, form, scheduleRecalculation]);

  // Auto-sync net income with EBITDA if not manually adjusted
  useEffect(() => {
    if (!netIncomeManuallyAdjusted) {
      const ebitda = form.watch("TTMEbitda") ?? 0;

      // Only update if EBITDA has actually changed to prevent infinite loops
      if (ebitda !== lastSyncedEbitda.current) {
        form.setValue("TTMNetIncome", ebitda);
        lastSyncedEbitda.current = ebitda;
      }
    }
  }, [form.watch("TTMEbitda"), netIncomeManuallyAdjusted, form]);

  // Reset manual adjustment flag when form is reset
  const resetForm = useCallback(() => {
    form.reset(defaults);
    setNetIncomeManuallyAdjusted(false);
  }, [form]);

  const submitApply = async () => {
    if (!resultCalculation || !lastEvalId) return;

    if (isLoggedIn) {
      if (
        await confirmDialog.openDialogAsync({
          title: "Create New Company",
          message:
            "Do you want to create a new company profile with this data or update your existing profile?",
          confirmText: "Create New",
          cancelText: "Update Existing",
        })
      ) {
        setLoading(true);
        try {
          await onboardBorrowerMutation.mutateAsync({
            query: {
              evalId: lastEvalId,
            },
          });
          window.location.href = `/borrower/profile`;
        } catch (err) {
          // Fail loud: on error the redirect below never runs, so without this the
          // spinner hangs forever with no feedback. Clear it and surface the failure
          // instead of swallowing it. (Forgejo #52)
          console.error("onboardBorrower mutation failed:", err);
          setLoading(false);
        }
      } else {
        setLoading(true);

        window.location.href = `/landing-apply?evalId=${lastEvalId}`;
      }
    } else {
      setLoading(true);

      if (nonWidget) {
        window.location.href = (baseUrl ?? "") +
          `/landing-apply?evalId=${lastEvalId}`;
      } else {
        window.open(
          (baseUrl ?? "") +
          `/landing-apply?evalId=${lastEvalId}`,
        );
      }
    }
  };

  // Asset inputs for the per-loan-type funding breakdown in the Wheel popover.
  // Read from form state (non-reactive getValues, matching the prior inline
  // behavior — the surrounding result view re-renders on recalculation). The
  // advance-rate math now lives once in the shared <FundingOptionBreakdown>.
  const breakdownInputs = {
    totalEquipment: form.getValues("TotalEquipment"),
    encumberedEquipment: form.getValues("EncumberedEquipment"),
    totalRealEstate: form.getValues("TotalRealEstate"),
    encumberedRealEstate: form.getValues("EncumberedRealEstate"),
    totalInventory: form.getValues("TotalInventory"),
    encumberedInventory: form.getValues("EncumberedInventory"),
    monthlyAr: form.getValues("MonthlyAr"),
    po: form.getValues("PO"),
  };

  const loanTypeDescriptions = loanTypes.reduce((acc, loanType) => {
    acc[loanType.key] = (
      <div>
        <p className="text-gray-600 whitespace-pre-line">
          {loanType.longDescription}
        </p>
        <FundingOptionBreakdown loanType={loanType.key} inputs={breakdownInputs} />
      </div>
    );
    return acc;
  }, {} as { [key: string]: React.ReactNode });

  // On a failed "See Funding Options" / "Get Started" submit, react-hook-form renders
  // the "Required" messages inline at the TOP of the form — but the submit button sits
  // below ~10 metric sliders, and RHF's default focus can't reach the react-select
  // fields (Funds Usage / Funding Timeline), so a validation failure left the user
  // staring at an unchanged screen: the "dead button" report on the anonymous CST.
  // Scroll the first invalid field into view so the feedback is where the user is looking.
  const handleInvalidSubmit = (
    _errors: unknown,
    event?: React.BaseSyntheticEvent,
  ) => {
    const formEl = (event?.target as HTMLElement | undefined)?.closest("form");
    // Defer one tick so the aria-invalid / FormMessage nodes have rendered.
    setTimeout(() => {
      const target = formEl?.querySelector<HTMLElement>(
        '[aria-invalid="true"], .text-destructive',
      );
      (target ?? formEl)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      target?.focus?.();
    }, 0);
  };

  const renderStepContent = (step: OnboardStep) => {
    switch (step) {
      case 1:
        return (
          <div className="bg-white rounded-lg shadow-md p-6 pt-4">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(async (values: FormOneType) => {
                  (values as Record<string, any>)["LoanType"] =
                    selectedLoanType;
                  // Only use debounce if we've already calculated once
                  if (resultCalculation) {
                    scheduleRecalculation(values, false);
                  } else {
                    calculateScore(values);
                  }
                }, handleInvalidSubmit)}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="ZipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700 font-medium">
                          Zip Code
                        </FormLabel>
                        <div className="flex gap-2">
                          <FormControl>
                            <Input {...field} className="w-full" />
                          </FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setIsLocating(true);
                              navigator.geolocation.getCurrentPosition(
                                async (position) => {
                                  try {
                                    const response = await fetch(
                                      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}`,
                                    );
                                    const data = await response.json();
                                    if (data.address && data.address.postcode) {
                                      form.setValue(
                                        "ZipCode",
                                        data.address.postcode,
                                      );
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error getting location:",
                                      error,
                                    );
                                  } finally {
                                    setIsLocating(false);
                                  }
                                },
                                (error) => {
                                  console.error(
                                    "Error getting location:",
                                    error,
                                  );
                                  setIsLocating(false);
                                },
                                {
                                  enableHighAccuracy: true,
                                  timeout: 5000,
                                  maximumAge: 0,
                                },
                              );
                            }}
                            disabled={isLocating}
                            className="hidden whitespace-nowrap"
                          >
                            {isLocating
                              ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                                  <span>Locating...</span>
                                </div>
                              )
                              : (
                                <div className="flex items-center gap-2">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <circle cx="12" cy="12" r="6" />
                                    <circle cx="12" cy="12" r="2" />
                                  </svg>
                                  <span>Use My Location</span>
                                </div>
                              )}
                          </Button>
                        </div>
                        <FormMessage className="text-sm" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="CompanyStage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700 font-medium">
                          Company Stage
                        </FormLabel>
                        <Select
                          aria-label="Company Stage"
                          value={field.value
                            ? { label: field.value, value: field.value }
                            : null}
                          onChange={(val: any) => field.onChange(val?.value)}
                          options={[
                            {
                              label: "Early (1-2 years)",
                              value: CompanyStage.EARLY,
                            },
                            {
                              label: "Growth (3-10 years)",
                              value: CompanyStage.GROWTH,
                            },
                            {
                              label: "Mature (10+ years)",
                              value: CompanyStage.MATURE,
                            },
                          ]}
                          className="w-full"
                        />
                        <FormMessage className="text-sm" />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="Industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 font-medium">
                        Industry
                      </FormLabel>
                      <Select
                        aria-label="Industry"
                        value={field.value
                          ? { label: field.value, value: field.value }
                          : null}
                        onChange={(val: any) => field.onChange(val?.value)}
                        options={industryOptions}
                        className="w-full"
                      />
                      <FormMessage className="text-sm" />
                    </FormItem>
                  )}
                />

                <h2 className="text-lg font-semibold mt-2">
                  Funds Needed
                </h2>
                <div className="space-y-3">
                  <MoneyField label="Funds Amount" name="LoanAmount" />
                  <div className="grid grid-cols-2 gap-4">
                    <SelectField
                      label="Funds Usage"
                      name="LoanUse"
                      options={loanUseOptions}
                    />
                    <SelectField
                      label="Funding Timeline"
                      name="FundingTimeline"
                      options={fundingTimelineOptions}
                    />
                  </div>
                </div>
                <h2 className="text-lg font-semibold mt-2">
                  Company Metrics
                </h2>
                <div className="space-y-2">
                  <div className="grid grid-cols-1">
                    {screenOneMetrics.map(renderMetricField)}
                  </div>
                </div>

                <Button
                  disabled={fundingOptionsQuery.isPending}
                  type="submit"
                  className="w-full text-lg font-medium p-6 bg-green-500 hover:bg-green-600 mt-4"
                >
                  {fundingOptionsQuery.isPending
                    ? "Calculating..."
                    : (resultCalculation
                      ? "Recalculate"
                      : "See Funding Options")}
                </Button>
              </form>
            </Form>
          </div>
        );
      case 3:
        return (
          <div className="bg-white rounded-lg shadow-md p-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(async (values: FormOneType) => {
                  (values as Record<string, any>)["LoanType"] =
                    selectedLoanType;
                  calculateScore(values);
                }, handleInvalidSubmit)}
              >
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold mb-4">
                    Tell us about your finances
                  </h2>
                  <div className="grid grid-cols-1">
                    {screenOneMetrics.map(renderMetricField)}
                  </div>
                </div>

                {currentStep == 1
                  ? (
                    <Button
                      disabled={fundingOptionsQuery.isPending}
                      type="submit"
                      className="w-full text-lg font-medium p-6 bg-green-500 hover:bg-green-600 mt-4"
                    >
                      {fundingOptionsQuery.isPending
                        ? "Calculating..."
                        : "Get Started"}
                    </Button>
                  )
                  : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full text-lg font-medium p-6 mt-4"
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentStep(1);
                      }}
                    >
                      Back
                    </Button>
                  )}
              </form>
            </Form>
          </div>
        );
      case 2:
        return (
          <Form {...formTwo}>
            <form>
              <div className="">
                <Wheel
                  size={400}
                  selectedType={selectedLoanType}
                  onSelectType={(t) => {
                    // Only allow selection if the loan type is in the funding options
                    const loanTypeId = loanTypes.find((x) => x.name == t)?.key;
                    if (
                      !resultCalculation?.fundingOptions?.some((option) =>
                        option.loanType === loanTypeId
                      )
                    ) {
                      return;
                    }

                    secondForm.setValue("loanType", loanTypeId ?? "Abl");
                    const data = formOneLastData || {};
                    data["LoanType"] = loanTypeId;
                    setSelectedLoanType(loanTypeId);

                    // Find the funding option for the selected loan type
                    if (resultCalculation?.fundingOptions) {
                      const selectedOption = resultCalculation.fundingOptions
                        .find(
                          (option) => option.loanType === loanTypeId,
                        );
                      if (selectedOption) {
                        setTopFundingOption(selectedOption);
                      }
                    }
                  }}
                  slices={loanTypes.filter((x) => !x.excludeFromWheel).map((
                    loanType,
                  ) => ({
                    ...loanType,
                    disabled: resultCalculation?.fundingOptions
                      ? !resultCalculation.fundingOptions.some((option) =>
                        option.loanType === loanType.key
                      )
                      : false,
                  }))}
                  loanTypeDescriptions={loanTypeDescriptions}
                  onHoverType={(t) => setHoveredType(t)}
                />
                <FundabilityScoreCard
                  score={resultCalculation?.fundabilityScore}
                  narrative={resultCalculation?.narrative ?? []}
                />
                {resultCalculation && (
                  <div className="bg-primary text-white p-2 rounded-md mb-2 text-center">
                    <p>
                      Select slices on the loan wheel or rows in the table to
                      explore funding options.
                    </p>
                  </div>
                )}
                {!resultCalculation && <IndicativeLoanOptionsCard />}
                {!resultCalculation && (
                  <div className="bg-white rounded-lg shadow-md p-4 hidden">
                    <h2 className="text-lg font-semibold mb-2">
                      Enter your metrics to see funding options
                    </h2>
                    <div className="space-y-2">
                      {/* Ghost funding option 1 */}
                      <div className="p-4 rounded-lg bg-gray-50 animate-pulse">
                        <div className="flex items-center justify-between mb-2">
                          <div className="h-5 rounded w-1/3">
                            Funding Option 1
                          </div>
                          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded w-2/3 mb-2">
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="grid grid-cols-2 gap-2 flex-1">
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Estimated Interest Rate
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Term
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Potential Available
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Estimated Payment
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                          </div>
                          <div className="h-8 bg-gray-200 rounded w-24"></div>
                        </div>
                      </div>
                      {/* Ghost funding option 2 */}
                      <div className="p-4 rounded-lg bg-gray-50 animate-pulse">
                        <div className="flex items-center justify-between mb-2">
                          <div className="h-4 rounded w-1/3">
                            Funding Option 2
                          </div>
                          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded w-2/3 mb-2">
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="grid grid-cols-2 gap-2 flex-1">
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Estimated Interest Rate
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Term
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Potential Available
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                            <div>
                              <div className="h-3 rounded w-1/2 mb-1 text-xs whitespace-nowrap">
                                Estimated Payment
                              </div>
                              <div className="h-4 bg-gray-200 rounded w-1/3">
                              </div>
                            </div>
                          </div>
                          <div className="h-8 bg-gray-200 rounded w-24"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Add Loan Details Section */}
                {resultCalculation?.fundingOptions &&
                  resultCalculation.fundingOptions.length > 0 && (
                    <div className="bg-white rounded-lg shadow-md p-4">
                      <div className="flex flex-row">
                        <h2 className="text-lg font-semibold">
                          {resultCalculation.fundingOptions.length}{" "}
                          Funding Options
                        </h2>
                      </div>
                      <div className="text-[11px] text-gray-500 mb-2">
                        The results below show illustrative rates and terms from
                        matching lenders based on the inputs provided. These are
                        not firm offers or endorsements by any lender or this
                        site. A formal loan application is required to receive
                        firm offers of credit.
                      </div>
                      {!biggestFundingOptions?.highAmount ||
                        biggestFundingOptions.highAmount <
                        form.getValues("LoanAmount")
                        ? (
                          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
                            <p className="text-sm text-orange-700">
                              No single funding option is likely to get you{" "}
                              {currency(form.getValues("LoanAmount"))}. A
                              combination of funding options may be required.
                            </p>
                          </div>
                        )
                        : null}
                      <h2 className="text-black text-sm">
                        Click each option to learn more.
                      </h2>
                      <FundingOptionsTable
                        options={resultCalculation.fundingOptions ?? []}
                        matchedLenders={resultCalculation.matchedLenders ??
                          undefined}
                        selectedOption={topFundingOption}
                        sortable
                        loanAmount={form.getValues("LoanAmount")}
                        loanTypeDescriptions={loanTypeDescriptions}
                      />
                    </div>
                  )}
                {currentStep == 2 && lastEvalId && !fundingOptionsQuery.isPending
                  ? applicationCta.href
                    ? (
                      <Button
                        asChild
                        className="w-full text-lg font-medium p-6 bg-green-500 hover:bg-green-600 mt-2"
                      >
                        <a
                          href={applicationCta.href}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {applicationCta.label}
                          <ChevronRight width={18} height={18} />
                        </a>
                      </Button>
                    )
                    : (
                      <Button
                        onClick={form.handleSubmit(
                          async (values: FormOneType) => {
                            submitApply();
                          },
                        )}
                        className="w-full text-lg font-medium p-6 bg-green-500 hover:bg-green-600 mt-2"
                      >
                        {applicationCta.label}
                        <ChevronRight width={18} height={18} />
                      </Button>
                    )
                  : <div></div>}
                {(isMobile && currentStep == 2)
                  ? (
                    <Button
                      className="w-full text-lg font-medium p-6"
                      variant={"outline"}
                      onClick={() => setCurrentStep(1)}
                    >
                      <ChevronLeft width={18} height={18} />
                      Back
                    </Button>
                  )
                  : <div></div>}
              </div>
            </form>
          </Form>
        );
      case 4:
        return (
          <div className="space-y-2">
            <Button
              onClick={submitApply}
              className="w-full text-lg font-medium p-6 bg-green-500 hover:bg-green-600"
            >
              Get Started <ChevronRight width={18} height={18} />
            </Button>
          </div>
        );
    }
  };

  const calculateScore = async (
    values: Record<string, any>,
    shouldFindBestLoanType = true,
    requestGeneration = ++evaluationGeneration.current,
  ) => {
    setLoading(true);
    setFormOneLastData(values);

    values["ReferralTag"] = referralTag;
    values["UnencumberedEquipment"] = values["TotalEquipment"] -
      values["EncumberedEquipment"];
    values["UnencumberedInventory"] = values["TotalInventory"] -
      values["EncumberedInventory"];
    values["UnencumberedRealEstate"] = values["TotalRealEstate"] -
      values["EncumberedRealEstate"];

    setLoading(false);
    setLastEvalId(undefined);

    values["LoanUse"] = [values["LoanUse"]];

    let fundingOptions: LandingEvaluationResponse;
    try {
      fundingOptions = await fundingOptionsQuery.mutateAsync({
        body: values,
      });
    } catch {
      if (requestGeneration === evaluationGeneration.current) {
        setLastEvalId(undefined);
      }
      return;
    }
    if (requestGeneration !== evaluationGeneration.current) return;
    setResultCalculation(fundingOptions);

    // Find the top funding option based on criteria
    const loanAmount = values.LoanAmount;
    const matchedOptions =
      fundingOptions.fundingOptions?.filter((option) =>
        (fundingOptions.matchedLenders?.[option.loanType ?? LoanType.ABL] ??
          0) > 0
      ) || [];

    // Split options into PO/AR and non-PO/AR
    const poArOptions = matchedOptions.filter((option) =>
      option.loanType === LoanType.PURCHASE_ORDER ||
      option.loanType === LoanType.AR_FACTORING
    );
    const otherOptions = matchedOptions.filter((option) =>
      option.loanType !== LoanType.PURCHASE_ORDER &&
      option.loanType !== LoanType.AR_FACTORING
    );

    // Sort both groups by payment amount
    const sortedPoArOptions = poArOptions.sort((a, b) =>
      (a.lowPayment ?? 1000000) - (b.lowPayment ?? 1000000)
    );
    const sortedOtherOptions = otherOptions.sort((a, b) =>
      (a.lowPayment ?? 1000000) - (b.lowPayment ?? 1000000)
    );

    // First try to find a non-PO/AR option that covers the full amount
    const fullCoverageOtherOption = sortedOtherOptions?.find((option) =>
      (option.highAmount ?? 0) >= loanAmount
    );

    // If no full coverage in other options, try PO/AR options
    const fullCoveragePoArOption = sortedPoArOptions?.find((option) =>
      (option.highAmount ?? 0) >= loanAmount
    );

    // If still no full coverage, use the quickest option from other options first
    const quickestOtherOption = sortedOtherOptions?.[0];
    const quickestPoArOption = sortedPoArOptions?.[0];

    // Select the best option in order of preference:
    // 1. Full coverage non-PO/AR option
    // 2. Full coverage PO/AR option
    // 3. Quickest non-PO/AR option
    // 4. Quickest PO/AR option
    const bestOption = fullCoverageOtherOption ||
      fullCoveragePoArOption ||
      quickestOtherOption ||
      quickestPoArOption;

    setTopFundingOption(bestOption);
    setBiggestFundingOption(
      matchedOptions.sort((a, b) =>
        (b.highAmount ?? 0) - (a.highAmount ?? 0)
      )[0],
    );
    setSelectedLoanType(
      bestOption?.loanType ??
      matchedOptions.sort((a, b) =>
        (b.highAmount ?? 0) - (a.highAmount ?? 0)
      )[0]?.loanType ??
      LoanType.SBA,
    );
    setLastEvalId(fundingOptions.evaluationId ?? undefined);

    setOnboardState("calculated");
    setCurrentStep(2);
  };

  const renderMetricField = (
    metric: typeof screenOneMetrics[0],
    index: number,
  ) => {
    const isYearsInBusiness = metric.Name === "Years in Business";
    const isCurrencyField = !isYearsInBusiness;
    const isMonthlyDebtService = metric.Key === "MonthlyDebtService";
    const max = metric.Key === "TTMEbitda"
      ? form.watch("TTMRevenue") ?? metric.Max
      : metric.Key === "MonthlyAr"
        ? Math.max((form.watch("TTMRevenue") ?? 0) * 0.2, 100000)
        : metric.Key === "PO"
          ? Math.max((form.watch("TTMRevenue") ?? 0) * 0.2, 100000)
          : metric.Max;
    const step = isYearsInBusiness
      ? 1
      : metric.Step ?? (max < 500000 ? 10000 : 100000);

    return (
      <FormField
        key={metric.Key}
        control={form.control}
        name={metric.Key as any}
        render={({ field }) => (
          <FormItem className="">
            <div className="flex justify-between items-start">
              <div className="">
                <FormLabel className="text-base">
                  {index + 1}. {metric.Name}
                </FormLabel>
                <p className="text-xs">
                  {metric.Description}
                </p>
                {/* These were asked to be hidden */}
                {metric.Key === "TTMNetIncome" && !netIncomeManuallyAdjusted &&
                  (
                    <p className="text-xs text-blue-600 font-medium flex items-center gap-1 hidden">
                      <svg
                        className="w-3 h-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Synced with EBITDA
                    </p>
                  )}
                {metric.Key === "TTMNetIncome" && netIncomeManuallyAdjusted && (
                  <button
                    type="button"
                    onClick={() => {
                      setNetIncomeManuallyAdjusted(false);
                      const ebitda = form.watch("TTMEbitda") ?? 0;
                      form.setValue("TTMNetIncome", ebitda);
                      lastSyncedEbitda.current = ebitda;
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 underline hidden"
                  >
                    Reset to sync with Operating Profit
                  </button>
                )}
              </div>
            </div>
            {metric.HasSecondKey
              ? (
                <div className="space-y-1">
                  <div className="flex items-center space-x-2 mt-2">
                    <Slider
                      aria-label={metric.Name}
                      value={[field.value]}
                      onValueChange={(value) => {
                        field.onChange(value[0]);
                        // If total value is reduced below unencumbered value, adjust unencumbered value
                        const currentUnencumbered =
                          form.watch(metric.SecondKey as any) ?? 0;
                        if (currentUnencumbered > (value[0] ?? 0)) {
                          form.setValue(metric.SecondKey as any, value[0]);
                        }
                      }}
                      min={metric.Min}
                      max={metric.Max}
                      step={metric.Step ??
                        (metric.Max < 500000 ? 10000 : 100000)}
                      className="flex-1"
                    />
                    <div className="w-32">
                      <MoneyInput
                        aria-label={metric.Name}
                        className="h-[30px]"
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                          // If total value is reduced below unencumbered value, adjust unencumbered value
                          const currentUnencumbered =
                            form.watch(metric.SecondKey as any) ?? 0;
                          if (currentUnencumbered > e) {
                            form.setValue(metric.SecondKey as any, e);
                          }
                        }}
                      />
                    </div>
                  </div>
                  {field.value > 0 && (
                    <>
                      <p className="text-xs">
                        <b>{metric.SecondName}</b>
                      </p>
                      <div className="flex items-center space-x-2">
                        <Slider
                          aria-label={metric.SecondName}
                          value={[form.watch(metric.SecondKey as any) ?? 0]}
                          onValueChange={(value) => {
                            const totalValue = field.value ?? 0;
                            const unencumberedValue = Math.min(
                              value[0] ?? 0,
                              totalValue,
                            );
                            form.setValue(
                              metric.SecondKey as any,
                              unencumberedValue,
                            );
                          }}
                          min={metric.Min}
                          max={field.value ?? 0}
                          step={metric.Step ??
                            (metric.Max < 500000 ? 10000 : 100000)}
                          className="flex-1"
                        />
                        <div className="w-32">
                          <MoneyInput
                            aria-label={metric.SecondName}
                            className="h-[30px]"
                            value={form.watch(metric.SecondKey as any) ?? 0}
                            onChange={(e) => {
                              const totalValue = field.value ?? 0;
                              const unencumberedValue = Math.min(e, totalValue);
                              form.setValue(
                                metric.SecondKey as any,
                                unencumberedValue,
                              );
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )
              : (
                <div className="flex items-center space-x-2">
                  <Slider
                    aria-label={metric.Name}
                    value={[field.value]}
                    onValueChange={(value) => {
                      // For EBITDA, ensure it's not higher than revenue
                      if (metric.Key === "TTMEbitda") {
                        const revenue = form.watch("TTMRevenue") ?? 0;
                        const ebitda = Math.min(value[0] ?? 0, revenue);
                        field.onChange(ebitda);
                      } else if (metric.Key === "TTMNetIncome") {
                        // Mark net income as manually adjusted when user interacts with slider
                        setNetIncomeManuallyAdjusted(true);
                        field.onChange(value[0]);
                      } else if (metric.Key === "MonthlyAr") {
                        // For MonthlyAr, ensure it's not higher than 20% of revenue, but at least 100,000
                        const revenue = form.watch("TTMRevenue") ?? 0;
                        const maxAr = Math.max(revenue * 0.2, 100000);
                        const arValue = Math.min(value[0] ?? 0, maxAr);
                        field.onChange(arValue);
                      } else if (metric.Key === "PO") {
                        // For PO, ensure it's not higher than 20% of revenue, but at least 100,000
                        const revenue = form.watch("TTMRevenue") ?? 0;
                        const maxPo = Math.max(revenue * 0.2, 100000);
                        const poValue = Math.min(value[0] ?? 0, maxPo);
                        field.onChange(poValue);
                      } else {
                        field.onChange(value[0]);
                      }
                    }}
                    min={metric.Min}
                    max={max}
                    step={step}
                    className="flex-1"
                  />
                  <div className="w-32">
                    {isCurrencyField
                      ? (
                        <MoneyInput
                          aria-label={metric.Name}
                          className="h-[30px]"
                          value={field.value}
                          onChange={(e) => {
                            // For EBITDA, ensure it's not higher than revenue
                            if (metric.Key === "TTMEbitda") {
                              const revenue = form.watch("TTMRevenue") ?? 0;
                              const ebitda = Math.min(e, revenue);
                              field.onChange(ebitda);
                            } else if (metric.Key === "TTMNetIncome") {
                              // Mark net income as manually adjusted when user interacts with input
                              setNetIncomeManuallyAdjusted(true);
                              field.onChange(e);
                            } else if (metric.Key === "MonthlyAr") {
                              // For MonthlyAr, ensure it's not higher than 20% of revenue, but at least 100,000
                              const revenue = form.watch("TTMRevenue") ?? 0;
                              const maxAr = Math.max(revenue * 0.2, 100000);
                              const arValue = Math.min(e, maxAr);
                              field.onChange(arValue);
                            } else if (metric.Key === "PO") {
                              // For PO, ensure it's not higher than 20% of revenue, but at least 100,000
                              const revenue = form.watch("TTMRevenue") ?? 0;
                              const maxPo = Math.max(revenue * 0.2, 100000);
                              const poValue = Math.min(e, maxPo);
                              field.onChange(poValue);
                            } else {
                              field.onChange(e);
                            }
                          }}
                        />
                      )
                      : (
                        <Input
                          aria-label={metric.Name}
                          type="number"
                          min={metric.Min}
                          max={metric.Key === "MonthlyAr"
                            ? Math.max(
                              (form.watch("TTMRevenue") ?? 0) * 0.2,
                              100000,
                            )
                            : metric.Key === "PO"
                              ? Math.max(
                                (form.watch("TTMRevenue") ?? 0) * 0.2,
                                100000,
                              )
                              : metric.Max}
                          step={isYearsInBusiness
                            ? 1
                            : isMonthlyDebtService
                              ? 10000
                              : 250000}
                          value={field.value}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))}
                          placeholder={isCurrencyField ? "$0" : "0"}
                        />
                      )}
                  </div>
                </div>
              )}
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />
    );
  };

  if (onboardState === "done") {
    return <div>Done!</div>;
  }

  return (
    <div className="flex flex-col space-y-2 relative">
      <div className="w-full bg-primary text-white text-center text-4xl py-4 hidden">
      </div>

      {/* Progress Bar */}
      <div className="flex-col items-center mb-6 pb-2 hidden">
        <div className="flex items-center space-x-2">
          {[
            { step: 1, label: "Company Info" },
            { step: 2, label: "Financial Info" },
            { step: 3, label: "Health Rating" },
            { step: 4, label: "Funding Options" },
          ]
            .map(({ step, label }) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-3 h-3 rounded-full transition-all duration-300 relative ${step <= (currentStep + 1) ? "bg-green-500" : "bg-gray-200"
                    }`}
                >
                  <div className="absolute -translate-x-1/2 left-1/2 pt-3 whitespace-nowrap">
                    <span
                      className={`text-sm transition-all duration-300 ${step <= (currentStep + 1)
                          ? "text-green-500 font-medium"
                          : "text-gray-400"
                        }`}
                    >
                      {label}
                    </span>
                  </div>
                </div>
                {step < 4 && (
                  <div
                    className={`w-[120px] h-0.5 mx-2 transition-all duration-300 ${step <= currentStep ? "bg-green-500" : "bg-gray-200"
                      }`}
                  />
                )}
              </div>
            ))}
        </div>
      </div>

      <div className="container relative overflow-x-hidden pb-2">
        <EvaluationIntroBanner />
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{
            width: "200%",
          }}
        >
          {(isMobile ? [currentStep] : [1, 2]).map((step) => (
            <div
              key={step}
              className={isMobile ? ("w-1/2 px-3") : ("w-1/4 px-3")}
            >
              {renderStepContent(step as OnboardStep)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OnboardWidget;
